import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { FaSpinner } from "react-icons/fa";
import { fileSize } from "./fileMeta";

const extOf = (name = "") => String(name).split(".").pop().toLowerCase();
export const isImageFile = (f) => String(f?.contentType || "").startsWith("image/")
  || ["png", "jpg", "jpeg", "gif", "webp"].includes(extOf(f?.name));
export const isPdfFile = (f) => String(f?.contentType || "").includes("pdf") || extOf(f?.name) === "pdf";

const galleryItemShape = PropTypes.shape({
  key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  url: PropTypes.string,
  contentType: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  subtitle: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.string,
  thumbLabel: PropTypes.node,
  thumbSub: PropTypes.node,
  thumbMeta: PropTypes.node,
  thumbBadge: PropTypes.node,
});

const IconButton = ({ icon, title, onClick, href, disabled }) => {
  const cls = `tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-geist-sm tw-border-0 tw-bg-transparent tw-text-white/80 hover:tw-bg-white/10 hover:tw-text-white tw-transition-colors ${
    disabled ? "tw-opacity-40 tw-pointer-events-none" : "tw-cursor-pointer"}`;
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" title={title} aria-label={title} className={`${cls} tw-no-underline`}>
        <i className={`bi ${icon} tw-text-[16px]`} />
      </a>
    );
  }
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} disabled={disabled} className={cls}>
      <i className={`bi ${icon} tw-text-[16px]`} />
    </button>
  );
};

IconButton.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string,
  onClick: PropTypes.func,
  href: PropTypes.string,
  disabled: PropTypes.bool,
};

/**
 * Một mục trong dải dưới cùng. Có thumbLabel (vd hóa đơn Doc Search) thì vẽ thành
 * thẻ hồ sơ riêng — số, người bán, ngày, tiền, trạng thái — để nhìn ra đây là các
 * hóa đơn KHÁC chứ không phải trang con của file đang xem. Không có thì là ảnh thu nhỏ.
 */
const Thumb = ({ item, index, active, onClick }) => {
  const tip = [item.thumbLabel, item.thumbSub, item.thumbMeta, item.name].filter(Boolean).join("\n");
  if (item.thumbLabel) {
    return (
      <button type="button" onClick={onClick} title={tip} aria-current={active || undefined}
        className={`tw-relative tw-flex tw-w-56 tw-flex-shrink-0 tw-items-start tw-gap-2.5 tw-rounded-geist-md tw-border tw-border-solid tw-px-3 tw-py-2 tw-text-left tw-cursor-pointer tw-transition-colors ${
          active ? "tw-border-geist-accent tw-bg-geist-accent/15" : "tw-border-white/15 tw-bg-white/[0.04] hover:tw-border-white/35 hover:tw-bg-white/[0.08]"}`}>
        <i className="bi bi-file-earmark-pdf tw-mt-0.5 tw-text-[18px] tw-text-[#ff6b5b] tw-flex-shrink-0" />
        <span className="tw-min-w-0 tw-flex-1">
          <span className="tw-flex tw-items-center tw-gap-1.5">
            <span className="tw-truncate tw-text-[13px] tw-font-semibold tw-text-white">{item.thumbLabel}</span>
            {active && <span className="tw-rounded tw-bg-geist-accent tw-px-1.5 tw-text-[9px] tw-font-bold tw-uppercase tw-text-white tw-flex-shrink-0">Viewing</span>}
            {item.thumbBadge && !active && (
              <span className="tw-rounded tw-bg-[#067647] tw-px-1.5 tw-text-[9px] tw-font-bold tw-uppercase tw-text-white tw-flex-shrink-0">{item.thumbBadge}</span>
            )}
          </span>
          {item.thumbSub && <span className="tw-block tw-truncate tw-text-[11px] tw-text-white/75">{item.thumbSub}</span>}
          {item.thumbMeta && <span className="tw-block tw-truncate tw-text-[11px] tw-tabular-nums tw-text-white/50">{item.thumbMeta}</span>}
        </span>
        <span className="tw-absolute tw-right-2 tw-top-1.5 tw-text-[10px] tw-tabular-nums tw-text-white/35">{index + 1}</span>
      </button>
    );
  }
  return (
    <button type="button" onClick={onClick} title={tip} aria-current={active || undefined}
      className={`tw-relative tw-h-14 tw-w-20 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-geist-sm tw-border tw-border-solid tw-p-0 tw-cursor-pointer tw-transition-colors ${
        active ? "tw-border-geist-accent tw-ring-1 tw-ring-geist-accent" : "tw-border-white/15 tw-bg-white/5 hover:tw-border-white/40"}`}>
      {isImageFile(item) && item.url ? (
        <img src={item.url} alt="" className="tw-h-full tw-w-full tw-object-cover" />
      ) : (
        <span className="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-0.5 tw-text-white/70">
          <i className={`bi ${isPdfFile(item) ? "bi-file-earmark-pdf tw-text-[#ff6b5b]" : "bi-file-earmark"} tw-text-[18px]`} />
          <span className="tw-max-w-[72px] tw-truncate tw-px-1 tw-text-[9px]">{item.name}</span>
        </span>
      )}
    </button>
  );
};

Thumb.propTypes = {
  item: galleryItemShape.isRequired,
  index: PropTypes.number.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func,
};

// Thời gian hiệu ứng đóng — phải khớp tw-duration-200 bên dưới.
const EXIT_MS = 200;
// Trình duyệt không bắn onLoad cho iframe PDF thì cũng không để khung ẩn mãi.
const LOAD_FALLBACK_MS = 2500;
const FADE = "tw-transition-opacity tw-duration-300 tw-ease-out motion-reduce:tw-transition-none";

/** Khung PDF/ảnh: ẩn tới khi tải xong rồi mờ dần vào — chuyển file không chớp trắng. */
const FadeInFrame = ({ kind, url, name }) => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), LOAD_FALLBACK_MS);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={`tw-relative tw-h-full ${kind === "image" ? "tw-flex tw-items-center tw-justify-center tw-p-4" : "tw-px-4 tw-py-3"}`}>
      {!loaded && (
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-sm tw-text-white/60">
          <FaSpinner className="tw-animate-spin tw-mr-2" /> Loading…
        </div>
      )}
      {kind === "image" ? (
        <img src={url} alt={name} onLoad={() => setLoaded(true)} onError={() => setLoaded(true)}
          className={`tw-max-h-full tw-max-w-full tw-object-contain tw-shadow-2xl tw-bg-white ${FADE} ${loaded ? "tw-opacity-100" : "tw-opacity-0"}`} />
      ) : (
        <iframe title={name} src={url} onLoad={() => setLoaded(true)}
          className={`tw-h-full tw-w-full tw-rounded tw-border-0 tw-bg-white tw-shadow-2xl ${FADE} ${loaded ? "tw-opacity-100" : "tw-opacity-0"}`} />
      )}
    </div>
  );
};

FadeInFrame.propTypes = {
  kind: PropTypes.oneOf(["image", "pdf"]).isRequired,
  url: PropTypes.string.isRequired,
  name: PropTypes.string,
};

/** Vùng xem: ảnh / PDF / trạng thái tải / lỗi. */
const Viewer = ({ item }) => {
  if (!item) return null;
  if (item.loading) {
    return (
      <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-white/70 tw-text-sm">
        <FaSpinner className="tw-animate-spin tw-mr-2" /> Loading…
      </div>
    );
  }
  if (item.error || !item.url) {
    return (
      <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-px-6 tw-text-center tw-text-white/70">
        <i className="bi bi-exclamation-triangle tw-text-[28px]" />
        <div className="tw-text-sm">{item.error || "This file cannot be previewed."}</div>
      </div>
    );
  }
  if (isImageFile(item)) return <FadeInFrame key={item.url} kind="image" url={item.url} name={item.name} />;
  if (isPdfFile(item)) return <FadeInFrame key={item.url} kind="pdf" url={item.url} name={item.name} />;
  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-text-white/70">
      <i className="bi bi-file-earmark tw-text-[40px]" />
      <div className="tw-text-sm">No preview for this file type.</div>
      <a href={item.url} target="_blank" rel="noreferrer" className="tw-text-sm tw-font-semibold tw-text-white">Download</a>
    </div>
  );
};

Viewer.propTypes = {
  item: galleryItemShape,
};

/**
 * Xem file kiểu tệp đính kèm email: lớp phủ tối toàn màn hình, xem ảnh/PDF ngay
 * trong trang, mũi tên (hoặc ← →) chuyển file, dải thumbnail phía dưới, Esc để đóng.
 *
 * Props:
 *   show
 *   items        [{ key, name, url, contentType, size, subtitle, loading, error,
 *                   thumbLabel, thumbSub }] — thumbLabel: nhãn thumbnail thay tên file
 *                   (vd số hóa đơn), vì tên file hóa đơn chỉ là dãy mã số thuế
 *   index        file đang xem
 *   onIndexChange(i)
 *   onClose()
 *   side(item)   tuỳ chọn — panel thông tin bên phải (vd chi tiết hóa đơn + nút Add)
 *   title        tuỳ chọn — nhãn nhỏ trên tên file
 *   stripTitle   tuỳ chọn — tiêu đề dải dưới cùng (vd "Search results · 20 invoices"),
 *                để người xem biết dải đó là danh sách hồ sơ khác, không phải trang con
 *
 * Esc được bắt ở capture phase và chặn lan truyền: modal này có thể mở đè lên một
 * drawer cũng nghe Esc, và Esc chỉ được đóng lớp trên cùng.
 */
const FileGalleryModal = ({ show, items = [], index = 0, onIndexChange, onClose, side, title, stripTitle }) => {
  const dialogRef = useRef(null);
  const thumbsRef = useRef(null);

  // Mở: gắn vào DOM rồi mới bật hiệu ứng. Đóng: chạy hiệu ứng xong mới gỡ — trước
  // đây lớp phủ tối hiện/tắt tức thì nên chớp cả màn hình.
  const [mounted, setMounted] = useState(show);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (show) {
      setMounted(true);
      const t = setTimeout(() => setVisible(true), 20);
      return () => clearTimeout(t);
    }
    setVisible(false);
    const t = setTimeout(() => setMounted(false), EXIT_MS);
    return () => clearTimeout(t);
  }, [show]);

  // Lúc đang đóng, component cha thường đã đặt index về 0 — giữ file vừa xem để
  // hiệu ứng đóng không lóe sang file đầu danh sách.
  const lastIndex = useRef(index);
  if (show) lastIndex.current = index;

  const count = items.length;
  const safeIndex = Math.min(Math.max(show ? index : lastIndex.current, 0), Math.max(count - 1, 0));
  const item = items[safeIndex];

  const latest = useRef({});
  latest.current = { onClose, onIndexChange, count, safeIndex };

  useEffect(() => {
    if (!show) return undefined;
    const opener = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => dialogRef.current?.focus(), 40);

    const onKey = (e) => {
      const { onClose: close, onIndexChange: go, count: n, safeIndex: i } = latest.current;
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close?.(); }
      // Đang gõ trong ô nhập (panel bên phải) thì mũi tên là của ô nhập.
      const typing = ["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName);
      if (typing || n < 2) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); e.stopPropagation(); go?.((i - 1 + n) % n); }
      if (e.key === "ArrowRight") { e.preventDefault(); e.stopPropagation(); go?.((i + 1) % n); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, [show]);

  // Giữ thumbnail đang chọn trong tầm nhìn.
  useEffect(() => {
    if (!show) return;
    thumbsRef.current?.querySelector(`[data-thumb="${safeIndex}"]`)?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [show, safeIndex]);

  if (!mounted || !item) return null;

  const go = (delta) => onIndexChange?.((safeIndex + delta + count) % count);
  const meta = [item.subtitle, fileSize(item.size)].filter(Boolean).join(" · ");

  return createPortal(
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={item.name} tabIndex={-1}
      className={`geist-scope tw-fixed tw-inset-0 tw-z-[100080] tw-bg-[#121214] tw-outline-none tw-transition-opacity tw-duration-200 tw-ease-out motion-reduce:tw-transition-none ${
        visible ? "tw-opacity-100" : "tw-opacity-0 tw-pointer-events-none"}`}>
      <div className={`tw-flex tw-h-full tw-flex-col tw-transition-transform tw-duration-200 tw-ease-out motion-reduce:tw-transition-none ${
        visible ? "tw-translate-y-0" : "tw-translate-y-3"}`}>
      {/* Thanh trên: tên file, số thứ tự, tải về, mở tab mới, đóng */}
      <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-2.5 tw-border-0 tw-border-b tw-border-solid tw-border-white/10 tw-flex-shrink-0">
        <i className={`bi ${isPdfFile(item) ? "bi-file-earmark-pdf tw-text-[#ff6b5b]" : isImageFile(item) ? "bi-file-earmark-image tw-text-[#7cc4ff]" : "bi-file-earmark tw-text-white/70"} tw-text-[20px] tw-flex-shrink-0`} />
        <div className="tw-min-w-0 tw-flex-1">
          {title && <div className="tw-text-[11px] tw-uppercase tw-tracking-wider tw-text-white/50">{title}</div>}
          <div className="tw-truncate tw-text-sm tw-font-semibold tw-text-white" title={item.name}>{item.name}</div>
          {meta && <div className="tw-truncate tw-text-[12px] tw-text-white/60">{meta}</div>}
        </div>
        {count > 1 && <span className="tw-text-[12px] tw-tabular-nums tw-text-white/60 tw-flex-shrink-0">{safeIndex + 1} / {count}</span>}
        <div className="tw-flex tw-items-center tw-gap-1 tw-flex-shrink-0">
          <IconButton icon="bi-box-arrow-up-right" title="Open in new tab" href={item.url} disabled={!item.url} />
          <IconButton icon="bi-download" title="Download" href={item.url} disabled={!item.url} />
          <IconButton icon="bi-x-lg" title="Close (Esc)" onClick={onClose} />
        </div>
      </div>

      <div className="tw-flex tw-min-h-0 tw-flex-1">
        {/* Vùng xem + mũi tên */}
        <div className={`tw-relative tw-min-w-0 tw-flex-1 ${count > 1 ? "tw-px-14" : ""}`}>
          <Viewer item={item} />
          {count > 1 && (
            <>
              <button type="button" onClick={() => go(-1)} aria-label="Previous file" title="Previous (←)"
                className="tw-absolute tw-left-2 tw-top-1/2 -tw-translate-y-1/2 tw-inline-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-black/50 tw-text-white hover:tw-bg-black/70 tw-cursor-pointer">
                <i className="bi bi-chevron-left tw-text-[18px]" />
              </button>
              <button type="button" onClick={() => go(1)} aria-label="Next file" title="Next (→)"
                className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-inline-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-black/50 tw-text-white hover:tw-bg-black/70 tw-cursor-pointer">
                <i className="bi bi-chevron-right tw-text-[18px]" />
              </button>
            </>
          )}
        </div>

        {side && (
          <aside className="tw-hidden md:tw-flex tw-w-[360px] tw-flex-shrink-0 tw-flex-col tw-min-h-0 tw-bg-geist-bg">
            {side(item, safeIndex)}
          </aside>
        )}
      </div>

      {/* Dải các file/hồ sơ khác — có tiêu đề để không bị hiểu nhầm là trang con */}
      {count > 1 && (
        <div className="tw-border-0 tw-border-t tw-border-solid tw-border-white/10 tw-bg-[#0c0c0e] tw-flex-shrink-0">
          {stripTitle && (
            <div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-pt-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-white/50">
              <i className="bi bi-collection" /> {stripTitle}
            </div>
          )}
          <div ref={thumbsRef} className="tw-overflow-x-auto tw-px-4 tw-py-2.5">
          <div className="tw-flex tw-w-max tw-mx-auto tw-gap-2">
            {items.map((it, i) => (
              <span key={it.key || i} data-thumb={i}>
                <Thumb item={it} index={i} active={i === safeIndex} onClick={() => onIndexChange?.(i)} />
              </span>
            ))}
          </div>
          </div>
        </div>
      )}
      </div>
    </div>,
    document.body
  );
};

FileGalleryModal.propTypes = {
  show: PropTypes.bool,
  items: PropTypes.arrayOf(galleryItemShape),
  index: PropTypes.number,
  onIndexChange: PropTypes.func,
  onClose: PropTypes.func,
  side: PropTypes.func,
  title: PropTypes.node,
  stripTitle: PropTypes.node,
};

export default FileGalleryModal;
